import React, { useEffect, useState } from 'react';
import { StudyGuideBlockNav } from './study-guide-block-nav';
import { StudyGuideBlockArticle } from './study-guide-block-article';
import { FiVideo } from 'react-icons/fi';
import { StudyGuideBlockModal } from './study-guide-block-modal';
import { useClient } from 'jsonapi-react';
import { useParams } from 'react-router-dom';
import { studyPlanEndpoints } from '@/pages/learning-path/block-endpoints';
import { Loader } from '@/components/custom/loader';
import { EmptyState } from '@/components/custom/empty-state';
import { DashboardContent } from '@/layouts/dashboard';
import { BadgeTruncate } from '@/components/ui/badge';
import { Dialog, DialogTrigger } from '@/components/ui/dialog';
import { GuideBlock, GuideBlockBtn, GuideBlockContent, GuideBlockHeader, GuideBlockHeaderContent, GuideBlockTitle } from '@/components/custom/guide';
import { isVimeoUrl, stripDescriptorCode, cn } from '@/lib/utils';
import usePlayfulStudent from '@/hooks/usePlayfulStudent';

export function StudyGuideBlock({ endpoints, backRoute = '/guia-de-estudos', backLabel = 'Guia de estudos' } = {}) {
  const playful = usePlayfulStudent();
  const [finished, setFinished] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [video, setVideo] = useState(null);
  const [scormData, setScormData] = useState(null);
  const [sections, setSections] = useState([]);
  const [activeSectionIndex, setActiveSectionIndex] = useState(0);
  const [loading, setLoading] = useState(false);
  const { blockId } = useParams();
  const endpointsForBlock = endpoints || studyPlanEndpoints(blockId);

  const client = useClient();

  const fetchData = () => {
    setLoading(true);
    Promise.all([getStudyPlanVideoById(), getScormData()]).then(([video, scorm]) => {
      const sortedScorm = sortScormDataByScormId(scorm);

      const isAssessment = item => ['questions', 'quiz_cards'].includes(item.scorm.scorm_type);
      const questionsScorm = sortedScorm.filter(isAssessment);
      const otherScorm = sortedScorm.filter(item => !isAssessment(item));

      const sections = [...otherScorm, ...questionsScorm].map(section => ({
        id: section.id,
        title:
          section.scorm.scorm_type === 'introduction'
            ? 'Introdução'
            : section.scorm.scorm_type === 'questions'
              ? 'Avaliação de conhecimento'
              : section.scorm.scorm_type === 'quiz_cards'
                ? 'Avaliação (quiz)'
                : section.scorm.scorm_type === 'content_carousel'
                  ? 'Conteúdo guiado'
                  : section.scorm.scorm_type === 'fill_in_the_blank'
                    ? 'Complete a frase'
                    : section.scorm.scorm_type === 'sequence_order'
                      ? 'Ordene a sequência'
                      : section.scorm.scorm_type === 'categorize'
                        ? 'Classifique'
                        : section.scorm.scorm_type === 'true_false_rapid'
                          ? 'Verdadeiro ou falso'
                          : stripDescriptorCode(section.scorm.title),
        done: section.done,
        isActive: false
      }));

      setSections(sections);
      setVideo(video);
      setScormData([...otherScorm, ...questionsScorm]);
      getIndexOfActiveSession([...otherScorm, ...questionsScorm]);
    });
    setLoading(false);
  };

  const getStudyPlanVideoById = async () => {
    const response = await client.fetch(endpointsForBlock.block);
    return response.data;
  };

  const getScormData = async () => {
    const response = await client.fetch(endpointsForBlock.sections);
    return response.data;
  };

  const getIndexOfActiveSession = scorm => {
    const index = scorm.findIndex(s => !s.done);
    if (index === -1) setActiveSectionIndex(0);
    else setActiveSectionIndex(index);

    const newScorm = scorm.map((s, i) => (i === activeSectionIndex ? { ...s, isActive: true } : { ...s, isActive: false }));
    setScormData(newScorm);
  };

  const sortScormDataByScormId = scorm => {
    return scorm.sort((a, b) => a.scorm.id - b.scorm.id);
  };

  useEffect(() => {
    fetchData();
  }, []);

  const breadcrumbs = block => ({
    title: 'Aprendizado Dinâmico',
    nav: [
      {
        // Nível único: a trilha em que o aluno está (nome do catálogo) → volta pra visão da trilha.
        // Fallback pro label legado quando o player roda fora da trilha (Guia de estudos).
        // O nome do bloco fica só no título do conteúdo (GuideBlockTitle), sem crumb duplicado.
        route: backRoute,
        name: block?.['learning-path-name'] || backLabel,
        isActive: false
      }
    ]
  });

  const hasVideo = isVimeoUrl(video?.video?.['video-url']);

  return (
    <DashboardContent breadcrumbs={breadcrumbs(video)}>
      {loading && <Loader />}

      {video && !loading && (
        <GuideBlock>
          {/* Dialog controlado (open/onOpenChange) e sem depender de estar dentro do
              header: assim tanto o botão "Conteúdo em vídeo" do header quanto o CTA
              "Assista a aula disponível" do EmptyState (quando ainda não há seções
              de SCORM geradas) conseguem abrir o mesmo modal de vídeo. Antes o Dialog
              ficava só dentro do header (sem open/onOpenChange), então o CTA do
              EmptyState só setava um state que nada escutava — clique não fazia nada. */}
          <Dialog
            open={showModal}
            onOpenChange={setShowModal}
          >
            <GuideBlockHeader>
              <GuideBlockHeaderContent>
                <GuideBlockTitle>{stripDescriptorCode(video?.taxonomy?.name)}</GuideBlockTitle>

                <BadgeTruncate
                  variant="primary-light"
                  items={video.video?.taxonomies}
                  oneLine
                />
              </GuideBlockHeaderContent>

              {hasVideo && (
                <DialogTrigger asChild>
                  <GuideBlockBtn
                    type="button"
                    className={playful ? cn('rounded-full border border-[var(--color-secondary)] bg-white px-4 py-2 font-bold text-[var(--color-secondary)] no-underline shadow-card transition-transform active:scale-[0.97] [&>svg]:stroke-2') : undefined}
                  >
                    <FiVideo /> Conteúdo em vídeo
                  </GuideBlockBtn>
                </DialogTrigger>
              )}
            </GuideBlockHeader>

            <GuideBlockContent>
              {scormData && scormData.length > 0 ? (
                <>
                  <StudyGuideBlockNav
                    data={sections}
                    finished={finished}
                    setActiveSectionIndex={setActiveSectionIndex}
                    activeSectionIndex={activeSectionIndex}
                  />

                  <StudyGuideBlockArticle
                    scorm={scormData[activeSectionIndex]}
                    study_plan_video_id={blockId}
                    endpoints={endpointsForBlock}
                    backRoute={backRoute}
                    introduction_image={video.video?.['introduction-image']}
                    last={activeSectionIndex === scormData.length - 1}
                    cta={() => setShowModal(true)}
                    allSectionsDone={scormData.findIndex(s => !s.done) === -1}
                    allSections={scormData}
                    setActiveSectionIndex={setActiveSectionIndex}
                    activeSectionIndex={activeSectionIndex}
                    setSections={setSections}
                    setFinished={setFinished}
                  />
                </>
              ) : (
                <EmptyState
                  type="data"
                  title="Seu conteúdo ainda está sendo gerado"
                  text="A nossa inteligência artificial está preparando o conteúdo para você. Aguarde um momento e tente novamente."
                  cta={hasVideo ? { text: 'Assista a aula disponível', onClick: () => setShowModal(true) } : undefined}
                />
              )}
            </GuideBlockContent>

            {hasVideo && (
              <StudyGuideBlockModal
                show={showModal}
                onClose={() => setShowModal(false)}
                video={video.video}
              />
            )}
          </Dialog>
        </GuideBlock>
      )}
    </DashboardContent>
  );
}
